/**
 * Active filter chips for vehicle search (Milestone 4).
 */
import { FAIR_PRICE_LABELS_ES } from "../../constants/automapFairPriceConstants";
import { RISK_LEVEL_LABELS_ES } from "../../constants/automapVehicleRiskConstants";
import { formatTemplatePrice } from "../../clientViewRuntime/templates/autoclaroHomeUtils";
import { parseVehicleSearchParams } from "./vehicleSearchParamsUrl";

function formatPriceChip(value) {
  if (value == null) return null;
  return formatTemplatePrice(value, "COP").replace(" COP", "");
}

/**
 * @returns {Array<{ key: string, label: string, removeKey?: string }>}
 */
export function buildVehicleSearchFilterChips(params) {
  const p = parseVehicleSearchParams(params);
  const chips = [];

  if (p.keyword) chips.push({ key: "keyword", label: `Búsqueda: ${p.keyword}` });
  if (p.city) chips.push({ key: "city", label: p.city });
  if (p.make) chips.push({ key: "make", label: p.make });
  if (p.model) chips.push({ key: "model", label: p.model });
  if (p.bodyType) chips.push({ key: "bodyType", label: p.bodyType.toUpperCase() });
  if (p.transmission) {
    const label =
      p.transmission === "automatic"
        ? "Automático"
        : p.transmission === "manual"
          ? "Mecánica"
          : p.transmission;
    chips.push({ key: "transmission", label });
  }
  if (p.fuelType) chips.push({ key: "fuelType", label: p.fuelType });
  p.fuelTypes?.forEach((fuelType) => {
    chips.push({
      key: `fuel:${fuelType}`,
      removeKey: "fuelTypeValue",
      removeValue: fuelType,
      label: fuelType,
    });
  });

  if (p.priceMinCop != null || p.priceMaxCop != null) {
    const minLabel = p.priceMinCop ? formatPriceChip(p.priceMinCop) : "sin mínimo";
    const maxLabel = p.priceMaxCop ? formatPriceChip(p.priceMaxCop) : "sin máximo";
    chips.push({ key: "price", label: `${minLabel} – ${maxLabel}` });
  }

  if (p.mileageMaxKm != null) {
    chips.push({ key: "mileageMaxKm", label: `Hasta ${p.mileageMaxKm.toLocaleString("es-CO")} km` });
  }
  if (p.yearMin != null) chips.push({ key: "yearMin", label: `Desde ${p.yearMin}` });
  if (p.yearMax != null) chips.push({ key: "yearMax", label: `Hasta ${p.yearMax}` });

  if (p.dealerVerified) chips.push({ key: "dealerVerified", label: "Concesionarios verificados" });

  p.fairPriceLabels?.forEach((label) => {
    chips.push({
      key: `fairPrice:${label}`,
      removeKey: "fairPriceLabel",
      removeValue: label,
      label: FAIR_PRICE_LABELS_ES[label] || label,
    });
  });

  p.riskLevels?.forEach((level) => {
    chips.push({
      key: `risk:${level}`,
      removeKey: "riskLevel",
      removeValue: level,
      label: RISK_LEVEL_LABELS_ES[level] || level,
    });
  });

  if (p.hideHighRisk) chips.push({ key: "hideHighRisk", label: "Bajo riesgo" });
  if (p.recentlyConfirmedOnly) chips.push({ key: "recentlyConfirmedOnly", label: "Confirmados recientemente" });
  if (p.completeInfoOnly) chips.push({ key: "completeInfoOnly", label: "Info completa" });
  if (p.availabilityStatus) chips.push({ key: "availabilityStatus", label: "Disponibilidad confirmada" });

  return chips;
}

export function removeFilterChip(params, chip) {
  const next = { ...parseVehicleSearchParams(params), page: 1 };

  switch (chip.key) {
    case "keyword":
      next.keyword = null;
      break;
    case "city":
      next.city = null;
      break;
    case "make":
      next.make = null;
      next.model = null;
      break;
    case "model":
      next.model = null;
      break;
    case "bodyType":
      next.bodyType = null;
      break;
    case "transmission":
      next.transmission = null;
      break;
    case "fuelType":
      next.fuelType = null;
      break;
    case "price":
      next.priceMinCop = null;
      next.priceMaxCop = null;
      break;
    case "mileageMaxKm":
      next.mileageMaxKm = null;
      break;
    case "yearMin":
      next.yearMin = null;
      break;
    case "yearMax":
      next.yearMax = null;
      break;
    case "dealerVerified":
      next.dealerVerified = false;
      break;
    case "hideHighRisk":
      next.hideHighRisk = false;
      break;
    case "recentlyConfirmedOnly":
      next.recentlyConfirmedOnly = false;
      break;
    case "completeInfoOnly":
      next.completeInfoOnly = false;
      break;
    case "availabilityStatus":
      next.availabilityStatus = null;
      break;
    default:
      if (chip.removeKey === "fuelTypeValue" && chip.removeValue) {
        next.fuelTypes = (next.fuelTypes || []).filter((value) => value !== chip.removeValue);
        if (!next.fuelTypes.length) next.fuelTypes = [];
      }
      if (chip.removeKey === "fairPriceLabel" && chip.removeValue) {
        next.fairPriceLabels = (next.fairPriceLabels || []).filter((v) => v !== chip.removeValue);
      }
      if (chip.removeKey === "riskLevel" && chip.removeValue) {
        next.riskLevels = (next.riskLevels || []).filter((v) => v !== chip.removeValue);
      }
      break;
  }

  return next;
}

export function buildFilteredResultStatusCopy(params, pagination, visibleCount) {
  const p = parseVehicleSearchParams(params);
  const total = pagination?.totalCount ?? pagination?.total;

  const parts = [];
  if (p.bodyType === "suv") parts.push("camionetas");
  else if (p.bodyType) parts.push(p.bodyType);
  if (p.transmission === "automatic") parts.push("automáticas");
  if (p.make) parts.push(p.make);
  if (p.model) parts.push(p.model);
  if (p.city) parts.push(`en ${p.city}`);

  if (total != null && total > 0) {
    const qualifier = parts.length ? parts.join(" ") : "vehículos";
    return {
      title: `${total.toLocaleString("es-CO")} ${qualifier} encontrados`.replace(/\s+/g, " ").trim(),
      subtitle:
        visibleCount < total
          ? `Mostrando ${visibleCount.toLocaleString("es-CO")} de ${total.toLocaleString("es-CO")}`
          : "Resultados verificados según tus filtros.",
      totalKnown: true,
    };
  }

  if (visibleCount > 0) {
    return {
      title: parts.length ? parts.join(" ") : "Vehículos disponibles",
      subtitle: `${visibleCount.toLocaleString("es-CO")} publicaciones visibles`,
      totalKnown: false,
    };
  }

  if (p.make || p.model || p.priceMaxCop) {
    const desc = [p.make, p.model, p.priceMaxCop ? `hasta ${formatPriceChip(p.priceMaxCop)}` : null]
      .filter(Boolean)
      .join(" ");
    return {
      title: `No encontramos resultados para ${desc}`,
      subtitle: "Prueba ampliar tu búsqueda o quitar algunos filtros.",
      totalKnown: false,
    };
  }

  return {
    title: "No encontramos vehículos con estos filtros.",
    subtitle: "Prueba ampliar tu búsqueda o quitar algunos filtros.",
    totalKnown: false,
  };
}
